import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import { DochazkaServiceApi } from "./DochazkaService";
import type { DochazkaClenView, SynchronizaceResponse, UzivatelAktivita } from "../generated/models/AkceDochazka";

// -----------------------------
// DOCHAZKA QUERIES
// -----------------------------

export const useDochazkaQuery = (akceId: number | null) => {
    return useQuery<DochazkaClenView[], Error>({
        queryKey: ["dochazka", akceId],
        queryFn: () => DochazkaServiceApi.getDochazka(akceId!),
        enabled: akceId !== null,
        staleTime: 15 * 1000,
    });
};

export const useUzivatelAktivitaQuery = (uzivatelId: number | null, rok?: number) => {
    return useQuery<UzivatelAktivita, Error>({
        queryKey: ["aktivita", uzivatelId, rok],
        queryFn: () => DochazkaServiceApi.aktivitaUzivatele(uzivatelId!, rok),
        enabled: uzivatelId !== null,
        staleTime: 60 * 1000,
    });
};

// -----------------------------
// DOCHAZKA MUTATIONS
// -----------------------------

export const useUlozDochazkuMutation = (akceId: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { uzivatelId: number; pritomen: boolean }[]>({
        mutationFn: (zaznamy) => DochazkaServiceApi.ulozDochazku(akceId, zaznamy),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dochazka", akceId] });
            showToast("Docházka byla úspěšně uložena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se uložit docházku.", "error");
        },
    });
};

export const useUzavritDochazkuMutation = (akceId: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SynchronizaceResponse, Error, void>({
        mutationFn: () => DochazkaServiceApi.uzavrit(akceId),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dochazka", akceId] });
            queryClient.invalidateQueries({ queryKey: ["akce", "detail", akceId] });
            // Zamerne BEZ poctu vlozeno/upraveno/odebrano: veliteli ta cisla nic
            // nerikaji (typicky vidi "+0 ~0 -0") a jen zastirala jedinou podstatnou
            // informaci — jestli je docházka zamcena, nebo odemcena.
            showToast("Docházka uzavřena", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se uzavřít docházku.", "error");
        },
    });
};

export const useOdemknoutDochazkuMutation = (akceId: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: () => DochazkaServiceApi.odemknout(akceId),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dochazka", akceId] });
            queryClient.invalidateQueries({ queryKey: ["akce", "detail", akceId] });
            // Zpusobilosti se odemcenim NEMENI — prepocitaji se az pri opetovnem
            // uzavreni. Hlaska to rika nahlas, aby velitel necekal okamzity dopad.
            showToast("Docházka odemčena, změny se propíšou až po uzavření", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odemknout docházku.", "error");
        },
    });
};
